/**
 * Message input area with text field and optional file attachments (up to 5).
 *
 * Validation:
 * - Body: max length from Constants.CHAT_MESSAGE_BODY_LENGTH_MAX (4000 chars)
 * - Attachments: max 5 per message; each max 10 MB; MIME-type whitelist (PDF and images: JPG, PNG, TIFF, BMP)
 *
 * The send button is disabled when:
 * - Body is blank AND no attachments are selected
 * - The Yup schema validation fails
 * - `disabled` prop is true (e.g. end-status reached)
 *
 * The paperclip button is disabled when 5 attachments are already selected.
 */

import React, {useRef, useState} from "react";
import {Box, Chip, IconButton, TextField, Tooltip} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import {NotificationI18N} from "@/translations";
import {Constants} from "@/api/constants";
import {useSnackbarEx} from "@/lib/snackbarex";
import type {ChatMessageDTO} from "./types";

/**
 * Allowed MIME types for file attachments.
 * Matches the backend FileSecurityValidator whitelist: PDF and images (JPG, PNG, TIFF, BMP).
 */
export const ALLOWED_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/tiff",
    "image/bmp",
    "application/pdf",
];

/**
 * Yup schema for the composer input (body + optional attachments list).
 * Exported so it can be tested in isolation.
 */
export const composerSchema = yup.object({
    body: yup.string()
        .max(
            Constants.CHAT_MESSAGE_BODY_LENGTH_MAX,
                NotificationI18N.chat.composer.bodyTooLong
        )
        .default(""),
    attachments: yup.array()
            .of(
                    yup.mixed<File>()
                            .test("fileSize", "attachment.tooLarge", (file) => {
                                if (!file) return true;
                                return file.size <= Constants.CHAT_ATTACHMENT_SIZE_MAX_BYTES;
                            })
                            .test("fileType", "attachment.invalidType", (file) => {
                                if (!file) return true;
                                return ALLOWED_MIME_TYPES.includes(file.type);
                            })
            )
            .max(Constants.CHAT_ATTACHMENTS_PER_MESSAGE_MAX)
            .default([]),
});

interface MessageComposerProps {
    /** Called when the user submits the message. */
    onSend: (body: string, attachments: File[]) => Promise<ChatMessageDTO>;
    /** When true, the send button is disabled (end-status or policy denial). */
    disabled?: boolean;
    /** Tooltip text shown when the composer is disabled. */
    disabledReason?: string;
}

const MessageComposer: React.FC<MessageComposerProps> = ({
    onSend,
    disabled = false,
    disabledReason,
}) => {
    const { t } = useTranslation();
    const {showMessage} = useSnackbarEx();
    const [body, setBody] = useState("");
    const [attachments, setAttachments] = useState<File[]>([]);
    const [attachmentError, setAttachmentError] = useState<string | undefined>(undefined);
    const [bodyError, setBodyError] = useState<string | undefined>(undefined);
    const [isSending, setIsSending] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const canSend = !disabled && !isSending && (body.trim().length > 0 || attachments.length > 0);
    const isAttachLimitReached = attachments.length >= Constants.CHAT_ATTACHMENTS_PER_MESSAGE_MAX;

    const validateFile = (file: File): boolean => {
        if (file.size > Constants.CHAT_ATTACHMENT_SIZE_MAX_BYTES) {
            setAttachmentError(
                    t(NotificationI18N.chat.attachment.tooLarge, {
                        max: Constants.CHAT_ATTACHMENT_SIZE_MAX_BYTES / (1024 * 1024)
                    })
            );
            return false;
        }
        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
            setAttachmentError(t(NotificationI18N.chat.attachment.invalidType));
            return false;
        }
        return true;
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        // IMPORTANT: Array.from(files) must happen BEFORE resetting the input value.
        // Setting input.value = "" invalidates the FileList object that `files` references,
        // so any Array.from() call after the reset would produce an empty array.
        const incoming = Array.from(files);

        // Reset file input so the same file can be re-selected after an error
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

        setAttachmentError(undefined);
        const available = Constants.CHAT_ATTACHMENTS_PER_MESSAGE_MAX - attachments.length;

        if (incoming.length > available) {
            showMessage({
                severity: "warning",
                summary: t(NotificationI18N.chat.composer.tooManyAttachments, {
                    max: Constants.CHAT_ATTACHMENTS_PER_MESSAGE_MAX
                }),
            });
        }

        const toAdd = incoming.slice(0, available);
        const validFiles: File[] = [];

        for (const file of toAdd) {
            if (validateFile(file)) {
                validFiles.push(file);
            }
        }

        if (validFiles.length > 0) {
            setAttachments((prev) => [...prev, ...validFiles]);
        }
    };

    const removeAttachment = (index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
        setAttachmentError(undefined);
    };

    const handleBodyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setBody(value);
        if (value.length > Constants.CHAT_MESSAGE_BODY_LENGTH_MAX) {
            setBodyError(t(NotificationI18N.chat.composer.bodyTooLong, {max: Constants.CHAT_MESSAGE_BODY_LENGTH_MAX}));
        } else {
            setBodyError(undefined);
        }
    };

    const handleSend = async () => {
        if (!canSend || bodyError || attachmentError) return;

        setIsSending(true);
        try {
            await onSend(body.trim(), attachments);
            setBody("");
            setAttachments([]);
            setAttachmentError(undefined);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        } catch {
            // Error handling is the responsibility of the parent (useConversation)
        } finally {
            setIsSending(false);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter" && !event.shiftKey && canSend) {
            event.preventDefault();
            void handleSend();
        }
    };

    const sendButton = (
        <IconButton
            size="small"
            color="primary"
            aria-label={t(NotificationI18N.chat.composer.send)}
            disabled={!canSend || !!bodyError || !!attachmentError}
            onClick={() => void handleSend()}
        >
            <SendIcon fontSize="small" />
        </IconButton>
    );

    return (
        <Box
            sx={{
                px: 1.5,
                py: 1,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
            }}
        >
            {/* Hidden file input — multiple to allow multi-select */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ALLOWED_MIME_TYPES.join(",")}
                style={{ display: "none" }}
                onChange={handleFileChange}
            />

            {/* Chip list of selected attachments */}
            {attachments.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mb: 0.5,
                    }}
                >
                    {attachments.map((file, index) => (
                            <Chip
                                    key={`${file.name}-${index}`}
                                    label={file.name}
                                    size="small"
                                    onDelete={() => removeAttachment(index)}
                            />
                    ))}
                </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5 }}>
                {/* Paperclip button — disabled when limit reached or composer disabled */}
                <Tooltip title={t(NotificationI18N.chat.composer.attach)}>
                    <span>
                        <IconButton
                            size="small"
                            disabled={disabled || isAttachLimitReached}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <AttachFileIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>

                {/* Text input */}
                <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    size="small"
                    variant="outlined"
                    placeholder={t(NotificationI18N.chat.composer.placeholder)}
                    value={body}
                    onChange={handleBodyChange}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    error={!!bodyError || !!attachmentError}
                    helperText={bodyError ?? attachmentError}
                />

                {/* Send button — wrap in Tooltip when disabled to show reason */}
                {disabled && disabledReason ? (
                    <Tooltip title={disabledReason}>
                        <span>{sendButton}</span>
                    </Tooltip>
                ) : (
                    sendButton
                )}
            </Box>
        </Box>
    );
};

export default MessageComposer;
